/**
 * FRONT-2550 round 3 — pure state machine for the Smart tagging card's
 * progress block: what a stored run looks like, how a poll answer moves it,
 * when it freezes, and what the block should show. No React, no DOM, no
 * clock reads — `now` is always passed in — so every rule is unit-testable.
 * `smart-tagging-run-store.ts` persists it, `use-smart-tagging-progress.ts`
 * feeds it poll answers, `smart-tagging-progress-block.tsx` renders the view.
 *
 * ── Where the numbers come from ──────────────────────────────────────────
 * `GET /api/okf/enrichment/activity?since=` counts every pass that ENDED
 * since the run started (`done`), split into `tagged` and `couldNotTag`. The
 * database answers, never the in-process queue: production runs more than one
 * replica and the one answering a poll is not the one holding the work.
 *
 * ── Phases ───────────────────────────────────────────────────────────────
 *  running → done   `done >= queued`. FROZEN: the final counts are stored and
 *                   no later answer changes them (an upload tagged an hour
 *                   later must never re-open yesterday's result).
 *  running → quiet  the count has not moved for QUIET_AFTER_MS of actual
 *                   polling. Time is accumulated poll-to-poll and a gap
 *                   longer than MAX_POLL_GAP_MS counts as zero, so a hidden
 *                   tab (polling pauses) or a reloaded page never ages it.
 *  quiet → running  the count moves again. Quiet is not terminal and not an
 *                   error (the same button recovers it), so it is muted.
 *  running|quiet → stopped
 *                   the person pressed "Stop tagging" (`stopRun`). FROZEN like
 *                   done, expiring 24 h later the same way; pressing "Tag
 *                   what's already here" again starts a NEW run that replaces
 *                   it. If the final read taken right after the stop shows
 *                   every item already finished, the run is `done`, not
 *                   `stopped`.
 * A failed poll is NOT a phase of the run: it is a flag on the view, because
 * it says nothing about whether tagging is progressing.
 */

export type RunPhase = 'running' | 'quiet' | 'done' | 'stopped';

export interface RunCounts {
  done: number;
  tagged: number;
  couldNotTag: number;
}

export interface StoredRun {
  /** The server's clock when the run was accepted (ISO-8601). */
  startedAt: string;
  /** How many items the server enqueued. Always >= 1: a run that queued
   *  nothing is never stored. */
  queued: number;
  /** The newest counts a poll returned for this run (clamped), or null before
   *  the first answer. This is what renders while a poll is in flight. */
  last: RunCounts | null;
  phase: RunPhase;
  /** Epoch ms the run reached `done` or `stopped`; absent until then. */
  finishedAt?: number;
  /** Polling time (ms) since `last.done` last changed. */
  quietMs: number;
  /** Epoch ms of the previous observed poll answer, null before the first. */
  lastPollAt: number | null;
}

/** No change for this much POLLING time, with items outstanding, is "quiet". */
export const QUIET_AFTER_MS = 3 * 60_000;
/** Two answers further apart than this were not a continuous poll (hidden
 *  tab, reload, laptop asleep): the gap between them counts as zero. The
 *  slowest poll is 15 s, so 45 s leaves room for a slow response. */
export const MAX_POLL_GAP_MS = 45_000;
/** A finished (done or stopped) run is forgotten this long after it ended. */
export const FINISHED_TTL_MS = 24 * 60 * 60_000;

export function startRun(input: { startedAt: string; queued: number }): StoredRun {
  return {
    startedAt: input.startedAt,
    queued: input.queued,
    last: null,
    phase: 'running',
    quietMs: 0,
    lastPollAt: null,
  };
}

/** Keeps the three numbers consistent with each other and with `queued`:
 *  `done` never exceeds `queued`, and `tagged + couldNotTag === done`. (An
 *  upload tagged inside the same window can push the raw count past `queued`;
 *  the card rounds that away rather than show "5 of 4".) */
function clampCounts(raw: RunCounts, queued: number): RunCounts {
  const done = Math.max(0, Math.min(raw.done, queued));
  const tagged = Math.max(0, Math.min(raw.tagged, done));
  return { done, tagged, couldNotTag: done - tagged };
}

/** Folds one real poll answer into the run. A finished run is frozen: the
 *  same object comes back. `now` is the answer's arrival time. */
export function applyObservation(run: StoredRun, answer: RunCounts, now: number): StoredRun {
  if (run.phase === 'done' || run.phase === 'stopped') return run;
  const counts = clampCounts(answer, run.queued);
  const gap = run.lastPollAt === null ? 0 : Math.max(0, now - run.lastPollAt);
  const continuous = gap <= MAX_POLL_GAP_MS ? gap : 0;
  const moved = counts.done !== (run.last?.done ?? 0);
  const quietMs = moved ? 0 : run.quietMs + continuous;
  if (counts.done >= run.queued) {
    return { ...run, last: counts, phase: 'done', finishedAt: now, quietMs: 0, lastPollAt: now };
  }
  return {
    ...run,
    last: counts,
    phase: quietMs >= QUIET_AFTER_MS ? 'quiet' : 'running',
    quietMs,
    lastPollAt: now,
  };
}

/** The person pressed "Stop tagging". `answer` is the one final activity read
 *  taken right after the stop returned (null when that read failed — the last
 *  known counts stand). Freezes the run as `stopped`; a run that already ended
 *  is returned untouched, and one whose final read shows every item finished
 *  becomes `done` instead (there was nothing left to stop). */
export function stopRun(run: StoredRun, answer: RunCounts | null, now: number): StoredRun {
  if (run.phase === 'done' || run.phase === 'stopped') return run;
  if (answer && clampCounts(answer, run.queued).done >= run.queued) return applyObservation(run, answer, now);
  const last = answer ? clampCounts(answer, run.queued) : run.last;
  return { ...run, last, phase: 'stopped', finishedAt: now, quietMs: 0 };
}

/** null when a finished run is older than the TTL (the caller drops it). */
export function expireRun(run: StoredRun, now: number): StoredRun | null {
  if ((run.phase === 'done' || run.phase === 'stopped') && run.finishedAt !== undefined && now - run.finishedAt > FINISHED_TTL_MS) return null;
  return run;
}

export interface ProgressView {
  phase: RunPhase;
  queued: number;
  done: number;
  tagged: number;
  couldNotTag: number;
  /** The last poll failed — shown as its own muted line, never a stall. */
  fetchError: boolean;
  /** When a finished run finished (epoch ms) — the done state's third line. */
  finishedAt?: number;
}

/** What to render. Before the first answer the last known numbers (or the
 *  zeros of a run that has only just been accepted) stand in, so a poll in
 *  flight never flashes a different block. */
export function progressView(run: StoredRun, opts: { fetchError?: boolean } = {}): ProgressView {
  const c = run.last ?? { done: 0, tagged: 0, couldNotTag: 0 };
  return {
    phase: run.phase,
    queued: run.queued,
    done: c.done,
    tagged: c.tagged,
    couldNotTag: c.couldNotTag,
    // A frozen result has nothing left to check.
    fetchError: opts.fetchError === true && run.phase !== 'done' && run.phase !== 'stopped',
    ...(run.phase === 'done' && run.finishedAt !== undefined ? { finishedAt: run.finishedAt } : {}),
  };
}

// ── Persistence format ───────────────────────────────────────────────────

export function runStorageKey(userId: string): string {
  return `eddy:smart-tagging-run:${userId}`;
}

const isCount = (n: unknown): n is number => typeof n === 'number' && Number.isInteger(n) && n >= 0;

/** Accepts only a well-formed record; anything else is "no run" rather than a
 *  nonsense window driving the poll. */
export function parseStoredRun(raw: string | null): StoredRun | null {
  if (!raw) return null;
  try {
    const v = JSON.parse(raw) as Partial<StoredRun> | null;
    if (!v || typeof v.startedAt !== 'string' || Number.isNaN(Date.parse(v.startedAt))) return null;
    if (!isCount(v.queued) || v.queued < 1) return null;
    if (v.phase !== 'running' && v.phase !== 'quiet' && v.phase !== 'done' && v.phase !== 'stopped') return null;
    let last: RunCounts | null = null;
    if (v.last != null) {
      if (!isCount(v.last.done) || !isCount(v.last.tagged) || !isCount(v.last.couldNotTag)) return null;
      last = clampCounts(v.last, v.queued);
    }
    if (v.phase === 'stopped' && typeof v.finishedAt !== 'number') return null;
    if (v.phase === 'done' && (typeof v.finishedAt !== 'number' || last === null)) return null;
    return {
      startedAt: v.startedAt,
      queued: v.queued,
      last,
      phase: v.phase,
      ...(typeof v.finishedAt === 'number' ? { finishedAt: v.finishedAt } : {}),
      quietMs: typeof v.quietMs === 'number' && v.quietMs >= 0 ? v.quietMs : 0,
      lastPollAt: typeof v.lastPollAt === 'number' ? v.lastPollAt : null,
    };
  } catch {
    return null;
  }
}
